import { useState, FormEvent } from 'react'
import { AuthError } from '@supabase/supabase-js'

interface AuthFormProps {
  onSignUp: (email: string, password: string) => Promise<{ error: AuthError | null }>
  onSignIn: (email: string, password: string) => Promise<{ error: AuthError | null }>
  onSignInWithGoogle: () => Promise<void>
}

export function AuthForm({ onSignUp, onSignIn, onSignInWithGoogle }: AuthFormProps) {
  const [isSignUp, setIsSignUp] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setMessage(null)
    setLoading(true)

    try {
      if (isSignUp) {
        const { error } = await onSignUp(email, password)
        if (error) {
          setError(error.message)
        } else {
          setMessage('Check your email for a confirmation link!')
        }
      } else {
        const { error } = await onSignIn(email, password)
        if (error) {
          setError(error.message)
        }
      }
    } finally {
      setLoading(false)
    }
  }

  const handleGoogleSignIn = async () => {
    setError(null)
    setGoogleLoading(true)
    try {
      await onSignInWithGoogle()
    } catch (err) {
      setError('Failed to sign in with Google')
      setGoogleLoading(false)
    }
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
          <h1>Readl8r</h1>
          <p>Sync your reading across all devices</p>
        </div>

        {/* Google Sign In Button */}
        <button
          type="button"
          className="google-btn"
          onClick={handleGoogleSignIn}
          disabled={googleLoading || loading}
        >
          {googleLoading ? (
            <span className="loading-spinner dark" />
          ) : (
            <>
              <svg className="google-icon" viewBox="0 0 24 24" width="20" height="20">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </>
          )}
        </button>

        {/* Divider */}
        <div className="auth-divider">
          <span>or</span>
        </div>

        <form className="auth-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              disabled={loading || googleLoading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Your password"
              required
              minLength={6}
              disabled={loading || googleLoading}
            />
          </div>

          {error && (
            <div className="auth-error">
              {error}
            </div>
          )}

          {message && (
            <div className="auth-message">
              {message}
            </div>
          )}

          <button type="submit" className="auth-submit" disabled={loading || googleLoading}>
            {loading ? (
              <span className="loading-spinner" />
            ) : isSignUp ? (
              'Create Account'
            ) : (
              'Sign In with Email'
            )}
          </button>
        </form>

        <div className="auth-toggle">
          {isSignUp ? (
            <>
              Already have an account?{' '}
              <button type="button" onClick={() => setIsSignUp(false)}>
                Sign In
              </button>
            </>
          ) : (
            <>
              Don't have an account?{' '}
              <button type="button" onClick={() => setIsSignUp(true)}>
                Sign Up
              </button>
            </>
          )}
        </div>
      </div>

      <style>{`
        .auth-container {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1rem;
          background: var(--bg-primary, #0a0a0a);
        }

        .auth-card {
          width: 100%;
          max-width: 400px;
          background: var(--bg-secondary, #141414);
          border-radius: 16px;
          padding: 2rem;
          box-shadow: 0 4px 24px rgba(0, 0, 0, 0.3);
        }

        .auth-header {
          text-align: center;
          margin-bottom: 1.5rem;
        }

        .auth-header svg {
          color: var(--accent, #f97316);
          margin-bottom: 1rem;
        }

        .auth-header h1 {
          font-size: 1.5rem;
          font-weight: 600;
          margin: 0 0 0.5rem 0;
          color: var(--text-primary, #ffffff);
        }

        .auth-header p {
          font-size: 0.875rem;
          color: var(--text-secondary, #888888);
          margin: 0;
        }

        /* Google Sign In Button */
        .google-btn {
          width: 100%;
          padding: 0.75rem 1rem;
          background: #ffffff;
          color: #1f1f1f;
          border: none;
          border-radius: 8px;
          font-size: 0.9375rem;
          font-weight: 500;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.75rem;
          min-height: 48px;
          transition: background 0.2s, box-shadow 0.2s;
          font-family: 'Roboto', -apple-system, BlinkMacSystemFont, sans-serif;
        }

        .google-btn:hover:not(:disabled) {
          background: #f8f8f8;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
        }

        .google-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .google-icon {
          flex-shrink: 0;
        }

        /* Divider */
        .auth-divider {
          display: flex;
          align-items: center;
          margin: 1.5rem 0;
          gap: 1rem;
        }

        .auth-divider::before,
        .auth-divider::after {
          content: '';
          flex: 1;
          height: 1px;
          background: var(--border, #2a2a2a);
        }

        .auth-divider span {
          font-size: 0.75rem;
          color: var(--text-tertiary, #555555);
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .auth-form {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .form-group label {
          font-size: 0.875rem;
          font-weight: 500;
          color: var(--text-secondary, #888888);
        }

        .form-group input {
          padding: 0.75rem 1rem;
          border: 1px solid var(--border, #2a2a2a);
          border-radius: 8px;
          background: var(--bg-primary, #0a0a0a);
          color: var(--text-primary, #ffffff);
          font-size: 1rem;
          transition: border-color 0.2s, box-shadow 0.2s;
        }

        .form-group input::placeholder {
          color: var(--text-tertiary, #555555);
        }

        .form-group input:focus {
          outline: none;
          border-color: var(--accent, #f97316);
          box-shadow: 0 0 0 3px rgba(249, 115, 22, 0.1);
        }

        .form-group input:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .auth-error {
          padding: 0.75rem 1rem;
          background: rgba(239, 68, 68, 0.1);
          border: 1px solid rgba(239, 68, 68, 0.3);
          border-radius: 8px;
          color: #ef4444;
          font-size: 0.875rem;
        }

        .auth-message {
          padding: 0.75rem 1rem;
          background: rgba(34, 197, 94, 0.1);
          border: 1px solid rgba(34, 197, 94, 0.3);
          border-radius: 8px;
          color: #22c55e;
          font-size: 0.875rem;
        }

        .auth-submit {
          padding: 0.75rem 1rem;
          background: transparent;
          color: var(--text-primary, #ffffff);
          border: 1px solid var(--border, #2a2a2a);
          border-radius: 8px;
          font-size: 0.9375rem;
          font-weight: 500;
          cursor: pointer;
          transition: background 0.2s, border-color 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 48px;
        }

        .auth-submit:hover:not(:disabled) {
          background: var(--bg-primary, #0a0a0a);
          border-color: var(--text-secondary, #888888);
        }

        .auth-submit:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .loading-spinner {
          width: 20px;
          height: 20px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        .loading-spinner.dark {
          border-color: rgba(0, 0, 0, 0.2);
          border-top-color: #1f1f1f;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        .auth-toggle {
          margin-top: 1.5rem;
          text-align: center;
          font-size: 0.875rem;
          color: var(--text-secondary, #888888);
        }

        .auth-toggle button {
          background: none;
          border: none;
          color: var(--accent, #f97316);
          font-size: 0.875rem;
          cursor: pointer;
          padding: 0;
          font-weight: 500;
        }

        .auth-toggle button:hover {
          text-decoration: underline;
        }
      `}</style>
    </div>
  )
}
